import React, {
  useEffect,
  useRef,
  useState,
  forwardRef,
  useImperativeHandle,
  useMemo,
} from 'react';
import {Modal} from 'react-native';
import {useTranslation} from 'react-i18next';
import AspectRatio from '@/components/basic/aspect-ratio';
import {cn, goTo} from '@/utils';
import styles from '../../vip.module.scss';
import ModalReceiveButton from '../../components/modal-receive-button';
interface VipUpgradeProps {
  zIndex?: number;
  onOpenComplete?: () => void;
}

export interface VipUpgradeRef {
  show: (exp: number) => void;
}

type BoxStatus = 'close' | 'open' | 'exp';
const Box = ({
  exp,
  status,
  show,
}: {
  exp?: number;
  status: BoxStatus;
  show?: boolean;
}) => {
  const image = useMemo(() => {
    const imgMap = {
      close: require('@/assets/imgs/vip/v2/box-close.webp'),
      open: require('@/assets/imgs/vip/v2/box-open.webp'),
      exp: require('@/assets/imgs/vip/v2/exp.webp'),
    };
    return imgMap[status];
  }, [status]);
  return (
    <div
      className={cn(
        'absolute top-0 -left-1 -right-1 -bottom-[.4375rem] bg-no-repeat bg-full',
        !show ? 'invisible' : '',
        status === 'exp' && show ? styles['animate-xp-scale-in'] : '',
      )}
      style={{
        backgroundImage: `url(${image})`,
      }}>
      {status === 'exp' && exp != null && (
        <div className="absolute bottom-[.4375rem] left-1 right-1 h-5 flex flex-col items-center justify-center text-white font-bold">
          XP {exp}
        </div>
      )}
    </div>
  );
};

const VipUpgrade = forwardRef<VipUpgradeRef, VipUpgradeProps>(
  ({zIndex}, ref) => {
    const [visible, setVisible] = useState(false);
    const modelRef = useRef<Modal>(null);
    const [exp, setExp] = useState(0);

    useImperativeHandle(ref, () => ({
      show: (_exp: number) => {
        setVisible(true);
        setExp(_exp);
      },
    }));

    useEffect(() => {
      if (visible && zIndex != null && modelRef.current) {
        let parent = modelRef.current as unknown as HTMLDivElement;
        while (parent && parent.parentElement !== document.body) {
          parent = parent.parentElement as HTMLDivElement;
        }
        (parent.children[0] as HTMLDivElement).style.zIndex = zIndex + '';
      }
    }, [visible, zIndex]);

    const {t, i18n} = useTranslation();
    const [disabledReceive, setDisabledReceive] = useState(false);
    const [boxOpen, setBoxOpen] = useState(false);
    const [boxExp, setBoxExp] = useState(false);
    const [boxExpReceive, setBoxExpReceive] = useState(false);
    const receiveStepRef = useRef(0);
    const handleOpen = () => {
      if (disabledReceive) {
        return;
      }
      if (receiveStepRef.current === 2) {
        setVisible(false);
        return;
      }
      setDisabledReceive(true);
      setBoxOpen(true);
      receiveStepRef.current = 1;
      setTimeout(() => {
        setBoxExp(true);
        setTimeout(() => {
          setDisabledReceive(false);
          setBoxExpReceive(true);
          receiveStepRef.current = 2;
          if (!window.location.pathname.includes('/index/me')) {
            goTo('Me');
          }
        }, 500);
      }, 500);
    };

    useEffect(() => {
      if (!visible) {
        setBoxExp(false);
        setBoxExpReceive(false);
        setBoxOpen(false);
        receiveStepRef.current = 0;
      }
    }, [visible]);

    const congratulationImg = useMemo(() => {
      const lang = i18n.language;
      const imgMap = {
        en_US: require('@/assets/imgs/vip/v2/congratulations-upgrade.webp'),
        hi_IN: require('@/assets/imgs/vip/v2/congratulations-upgrade-hi.webp'),
        te_IN: require('@/assets/imgs/vip/v2/congratulations-upgrade-te.webp'),
        ml_IN: require('@/assets/imgs/vip/v2/congratulations-upgrade-ml.webp'),
        ta_IN: require('@/assets/imgs/vip/v2/congratulations-upgrade-ta.webp'),
      };
      return imgMap[lang as keyof typeof imgMap] || imgMap.en_US;
    }, [i18n]);
    return (
      <Modal
        visible={visible}
        ref={modelRef}
        transparent
        statusBarTranslucent={false}
        animationType="fade">
        <div className="w-full h-full bg-black/50" onClick={() => handleOpen()}>
          <div
            className="size-0 invisible"
            style={{
              backgroundImage: `url(${require('@/assets/imgs/vip/v2/exp.webp')})`,
            }}
          />
          {!boxExpReceive ? (
            <>
              <div
                className={cn(
                  'absolute top-[calc(50%-7rem)] left-1/2 -translate-x-1/2 w-[23.4375rem] -translate-y-1/2',
                  visible && !boxExp
                    ? styles['animate-base-modal-show-50p']
                    : 'opacity-0 transition-opacity',
                )}>
                <AspectRatio ratio={375 / 472}>
                  <div
                    className="size-full bg-no-repeat bg-full"
                    style={{
                      backgroundImage: `url(${require('@/assets/imgs/vip/v2/person.webp')})`,
                    }}
                  />
                </AspectRatio>
              </div>
              <div
                className={cn(
                  'absolute top-[calc(50%+6rem)] left-1/2 -translate-x-1/2 -translate-y-1/2',
                  visible && !boxExp
                    ? styles['animate-base-modal-show-50p']
                    : 'opacity-0 transition-opacity',
                )}>
                <div className="py-[1.875rem] px-5 flex flex-col gap-6 rounded-xl border-2 w-[19.4375rem] border-white bg-gradient-to-b from-[#FFF7E1] to-white relative">
                  <div className="flex flex-col gap-3 items-center">
                    <h3 className="text-[#A93813] text-base">
                      {t('vip.label.vipUpgrade')}
                    </h3>
                    <div className="text-[#9C4B19] font-normal text-[0.625rem] flex flex-col">
                      <p>{t('vip.tip.upgrade.to')}</p>
                      <p>{t('vip.tip.upgrade.subtitle')}</p>
                    </div>
                  </div>
                  <div className="flex flex-col gap-3.5 items-center">
                    <ul className="flex flex-col list-disc list-inside text-[#9C4B19] text-xs">
                      <li>{t('vip.tip.upgrade.li1')}</li>
                      <li>{t('vip.tip.upgrade.li2')}</li>
                      <li>{t('vip.tip.upgrade.li3')}</li>
                    </ul>
                    <div className="w-[5.4375rem] h-[4.8125rem] relative overflow-visible">
                      <Box status="close" show={!boxOpen} />
                      <Box status="open" show={boxOpen} />
                    </div>
                  </div>

                  <ModalReceiveButton
                    onClick={() => handleOpen()}
                    disabled={disabledReceive}
                  />
                </div>
              </div>
            </>
          ) : null}
          {boxExp ? (
            <>
              <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 flex flex-col items-center justify-center gap-8">
                <div
                  className={cn(
                    'w-[16.9375rem] h-10 bg-full bg-no-repeat',
                    boxExpReceive ? '' : 'invisible',
                  )}
                  style={{
                    backgroundImage: `url(${congratulationImg})`,
                  }}
                />
                <div className="relative">
                  <div
                    className={cn(
                      'absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 size-[12.1875rem] rounded-full bg-[#DAAEF5] blur-[3.125rem] transition-opacity',
                      boxExpReceive ? 'opacity-100' : 'opacity-0',
                    )}
                  />
                  <div
                    className={cn(
                      'w-[12.0625rem] h-[12.375rem] bg-no-repeat bg-full relative',
                      styles['animate-xp-scale-in'],
                    )}
                    style={{
                      backgroundImage: `url(${require('@/assets/imgs/vip/v2/exp.webp')})`,
                    }}>
                    <div className="absolute h-[3.4375rem] bottom-[.9375rem] left-3 right-3 flex items-center justify-center text-white font-black text-[1.625rem]">
                      XP {exp}
                    </div>
                  </div>
                </div>
              </div>
            </>
          ) : null}
        </div>
      </Modal>
    );
  },
);

export default VipUpgrade;
